import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { storage, db } from '../lib/firebase';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { TicketAttachment } from '../types/customerTicket';

interface UploadAttachmentParams {
  file: File;
  ticketId: string;
  ticketTitle: string;
  ticketType: string;
  customerId: string;
  customerName: string;
  organizationId: string;
  uploadedBy: string;
  uploadedByName: string;
  uploadedByType: 'customer' | 'staff';
}

class CustomerTicketAttachmentService {
  /**
   * Feltölt egy fájlt a customer ticket-hez és létrehozza a dokumentumtár linket
   */
  async uploadAttachment(params: UploadAttachmentParams): Promise<TicketAttachment> {
    const {
      file,
      ticketId,
      ticketTitle,
      ticketType,
      customerId,
      customerName,
      organizationId,
      uploadedBy,
      uploadedByName,
      uploadedByType
    } = params;

    try {
      // 1. Fájl feltöltése Firebase Storage-ba
      const fileId = `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      const fileName = file.name;
      const storagePath = `customerTickets/${ticketId}/attachments/${fileId}_${fileName}`;
      const storageRef = ref(storage, storagePath);

      await uploadBytes(storageRef, file);
      const downloadURL = await getDownloadURL(storageRef);

      // 2. Dokumentumtár link létrehozása
      const documentData = {
        // Alapadatok
        fileName: fileName,
        fileUrl: downloadURL,
        fileSize: file.size,
        fileType: file.type,
        storagePath: storagePath,

        // Kapcsolatok
        customerId: customerId,
        customerName: customerName,

        // Kategorizálás
        category: 'customer_ticket',
        tags: ['customer_ticket'],

        // Ticket kontextus
        ticketId: ticketId,
        ticketTitle: ticketTitle,
        ticketType: ticketType,

        // Feltöltő
        uploadedBy: uploadedBy,
        uploadedByName: uploadedByName,
        uploadedByType: uploadedByType,
        uploadedAt: serverTimestamp(),

        // Szervezet
        organizationId: organizationId,

        // Státusz
        status: 'active'
      };

      // Dokumentumtárba mentés
      const docRef = await addDoc(collection(db, 'documents'), documentData);

      // 3. TicketAttachment objektum visszaadása
      const attachment: TicketAttachment = {
        id: fileId,
        name: fileName,
        url: downloadURL,
        size: file.size,
        type: file.type,
        uploadedBy: uploadedBy,
        uploadedByName: uploadedByName,
        uploadedAt: new Date(),
        documentId: docRef.id, // Link a dokumentumtárhoz
        storagePath: storagePath // Firebase Storage path (törléshez)
      };

      return attachment;
    } catch (error) {
      console.error('Error uploading attachment:', error);
      throw error;
    }
  }

  /**
   * Töröl egy mellékletet
   */
  async deleteAttachment(storagePath: string): Promise<void> {
    try {
      const storageRef = ref(storage, storagePath);
      await deleteObject(storageRef);
    } catch (error) {
      console.error('Error deleting attachment:', error);
      throw error;
    }
  }

  /**
   * Validálja a fájlt (méret, típus)
   */
  validateFile(file: File): { valid: boolean; error?: string } {
    // Max 10 MB
    const maxSize = 10 * 1024 * 1024;
    if (file.size > maxSize) {
      return { valid: false, error: 'A fájl mérete maximum 10 MB lehet' };
    }

    // Engedélyezett típusok
    const allowedTypes = [
      'image/jpeg',
      'image/jpg',
      'image/png',
      'image/gif',
      'image/webp',
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'text/plain'
    ];

    if (!allowedTypes.includes(file.type)) {
      return { 
        valid: false, 
        error: 'Nem támogatott fájltípus. Engedélyezett: képek, PDF, Word, Excel, szöveges fájlok' 
      };
    }

    return { valid: true };
  }

  /**
   * Formázza a fájlméretet olvasható formátumba
   */
  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  }
}

export const customerTicketAttachmentService = new CustomerTicketAttachmentService();
